/**
 * MasterDetailEditor — master/detail two-panel layout for the Single App editor.
 *
 * LEFT (master, resizable): underline Tabs (Measures/Dimensions/Variables) +
 *   toolbar (search, type filter, + New) + scrollable list.
 * RIGHT (detail, flex): renders the matching detail form for the selected item,
 *   or an empty state.
 *
 * The Analysis tab is intentionally NOT included here (removed per spec).
 * Edit model is UNCHANGED — auto-commit to the draft on every field change.
 */

import { useState, useMemo, useCallback, useEffect, useRef, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Replace, Plus, X, Trash2, Calculator, Ruler, Code, MousePointerClick } from "lucide-react";
import {
  Badge,
  Button,
  Field,
  FeedbackState,
  MultiSelect,
  SearchInput,
  Switch,
  Tabs,
  type SelectOption,
} from "@h2j-data-team/ui";
import {
  buildExpressionGraph,
  type GraphItemInput,
  type GraphItemKind,
  type QlikDimension,
  type QlikMeasure,
  type QlikVariable,
  type QlikApp,
  type QlikField,
} from "@repo/shared";
import { DETAIL_GRID, ExpressionDecomposition } from "@/components/qlik/expression-decomposition";
import { FindReplaceBar, type FindReplaceItem } from "@/components/qlik/find-replace-bar";
import { RowTags, TagFilter } from "@/components/qlik/item-tags";
import { ConfirmDialog, useConfirmDialog } from "@/components/shared";
import { ResizeHandle } from "@/components/resize-handle";
import { useFormatDate } from "@/hooks/use-format-date";
import { pickerMessages } from "@/lib/ui-labels";
import { useResizablePanelWidth } from "@/lib/use-resizable-panel-width";
import { cn } from "@/lib/utils";
import type { PendingMasterItemChange, PendingMasterItemCreate } from "@/stores/pending-changes";
import { MeasureDetailForm } from "./MeasureDetailForm";
import { DimensionDetailForm } from "./DimensionDetailForm";
import { VariableDetailForm } from "./VariableDetailForm";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type PanelTab = "measures" | "dimensions" | "variables";

interface MasterDetailEditorProps {
  connectionId: string;
  appId: string;
  currentApp: QlikApp | undefined;
  canUpdateApp: boolean;
  measures: QlikMeasure[] | undefined;
  dimensions: QlikDimension[] | undefined;
  variables: QlikVariable[] | undefined;
  fields: QlikField[] | undefined;
  /** From masterItemsHook */
  masterItemsHook: {
    pendingCreates: Map<string, PendingMasterItemCreate>;
    pendingChanges: Map<string, PendingMasterItemChange>;
    addChange: (change: PendingMasterItemChange) => void;
    removeChange: (connId: string, appId: string, itemId: string) => void;
    addCreate: (create: PendingMasterItemCreate) => void;
    removeCreate: (connId: string, appId: string, tempId: string) => void;
    updateCreate: (
      connId: string,
      appId: string,
      tempId: string,
      patch: Partial<PendingMasterItemCreate>,
    ) => void;
    getChange: (
      connId: string,
      appId: string,
      itemId: string,
    ) => PendingMasterItemChange | undefined;
  };
  /** From variablesHook */
  variablesHook: {
    pendingCreates: Map<string, import("@/stores/pending-changes").PendingVariableCreate>;
    pendingChanges: Map<string, import("@/stores/pending-changes").PendingVariableChange>;
    addChange: (change: import("@/stores/pending-changes").PendingVariableChange) => void;
    removeChange: (connId: string, appId: string, varId: string) => void;
    addCreate: (create: import("@/stores/pending-changes").PendingVariableCreate) => void;
    removeCreate: (connId: string, appId: string, tempId: string) => void;
    updateCreate: (
      connId: string,
      appId: string,
      tempId: string,
      patch: { name?: string; definition?: string; comment?: string },
    ) => void;
    getChange: (
      connId: string,
      appId: string,
      varId: string,
    ) => import("@/stores/pending-changes").PendingVariableChange | undefined;
  };
  /** Handlers from the route (wrapping addChange/addCreate with persistDraft) */
  handleSaveMeasureToCache: (
    measure: QlikMeasure,
    changes: {
      title?: string;
      description?: string;
      expression?: string;
      label?: string;
      tags?: string[];
      numberFormat?: string;
      color?: string;
    },
  ) => void;
  handleSaveDimensionToCache: (
    dimension: QlikDimension,
    changes: {
      title?: string;
      description?: string;
      fieldDefs?: string[];
      fieldLabels?: string[];
      grouping?: "N" | "H" | "C";
      labelExpression?: string;
      tags?: string[];
    },
  ) => void;
  handleSaveVariableToCache: (
    variable: QlikVariable,
    changes: { name?: string; definition?: string; comment?: string },
  ) => void;
  handleDeleteMeasureToCache: (measure: QlikMeasure) => void;
  handleDeleteDimensionToCache: (dimension: QlikDimension) => void;
  handleDeleteVariableToCache: (variable: QlikVariable) => void;
  initialTab?: PanelTab;
  initialItem?: string;
  onSelectionChange?: (tab: PanelTab, item: string | null) => void;
  /** Draft action-row state, lifted from the route. */
  pendingCount: number;
  /** Receives the Save button, where the focus returns if the backup dialog is cancelled. */
  onSaveAll: (trigger?: HTMLElement | null) => void;
  onDiscardAll: () => void;
  isSaving: boolean;
  saveDisabled: boolean;
  /** Flags da última análise do app (sem uso / quebrado). Somente visual. */
  analysisFlags?: AnalysisFlags;
}

// ---------------------------------------------------------------------------
// Analysis flags (from the app's most recent analysis) — visual only
// ---------------------------------------------------------------------------

export type AnalysisItemFlag = { unused: boolean; broken: boolean; indirect: boolean };

export type AnalysisFlags = {
  analyzedAt: string;
  dimensions: Map<string, AnalysisItemFlag>;
  measures: Map<string, AnalysisItemFlag>;
  variables: Map<string, AnalysisItemFlag>;
};

// ---------------------------------------------------------------------------
// Status dot
// ---------------------------------------------------------------------------

function StatusDot({
  isPendingDelete,
  isPending,
}: {
  isPendingDelete: boolean;
  isPending: boolean;
}) {
  const { t } = useTranslation("single-app");
  const [color, label] = isPendingDelete
    ? ["bg-status-danger-foreground", t("masterItems.statusPendingDelete")]
    : isPending
      ? ["bg-status-warning-foreground", t("masterItems.statusPending")]
      : ["bg-border", t("masterItems.statusSynced")];
  return (
    <span
      role="img"
      aria-label={label}
      className={cn("mt-1 h-1.5 w-1.5 shrink-0 rounded-full", color)}
    />
  );
}

function AnalysisBadge({
  flag,
  analyzedAtLabel,
}: {
  flag: AnalysisItemFlag;
  analyzedAtLabel: string;
}) {
  const { t } = useTranslation("single-app");
  if (!flag.broken && !flag.unused && !flag.indirect) return null;
  return (
    <span className="flex shrink-0 items-center gap-1">
      {flag.broken && (
        <Badge
          variant="danger"
          className="font-mono"
          title={t("analysisFlags.brokenTooltip", { date: analyzedAtLabel })}
        >
          {t("analysisFlags.broken")}
        </Badge>
      )}
      {flag.indirect && (
        <Badge
          variant="info"
          className="font-mono"
          title={t("analysisFlags.indirectTooltip", { date: analyzedAtLabel })}
        >
          {t("analysisFlags.indirect")}
        </Badge>
      )}
      {flag.unused && (
        <Badge
          variant="warning"
          className="font-mono"
          title={t("analysisFlags.unusedTooltip", { date: analyzedAtLabel })}
        >
          {t("analysisFlags.unused")}
        </Badge>
      )}
    </span>
  );
}

// ---------------------------------------------------------------------------
// One row of the master list: a real button selects the item; the row actions
// are sibling buttons, shown on hover and on focus (never display:none, which
// would take them out of the keyboard's reach).
// ---------------------------------------------------------------------------

function MasterRow({
  title,
  secondary,
  tags,
  selectedTags,
  mono,
  selected,
  isPending,
  isPendingDelete,
  flag,
  analyzedAtLabel,
  canDelete,
  onSelect,
  onDiscard,
  onDelete,
}: {
  title: string;
  secondary?: string;
  tags?: string[];
  selectedTags?: string[];
  /** The title is an identifier (a variable name). */
  mono?: boolean;
  selected: boolean;
  isPending: boolean;
  isPendingDelete: boolean;
  flag?: AnalysisItemFlag;
  analyzedAtLabel: string;
  canDelete: boolean;
  onSelect: () => void;
  onDiscard: () => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation("single-app");
  const name = title || t("editor.list.untitled");
  return (
    <li className={cn("group relative hover:bg-muted", selected && "bg-muted")}>
      <button
        type="button"
        aria-current={selected ? "true" : undefined}
        onClick={onSelect}
        className="flex w-full items-start gap-2 px-3 py-2.5 text-left outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
      >
        <StatusDot isPendingDelete={isPendingDelete} isPending={isPending} />
        <span className="min-w-0 flex-1">
          <span
            className={cn(
              "block truncate text-[13px] font-medium leading-tight",
              mono && "font-mono",
              isPendingDelete ? "text-faint line-through" : "text-ink",
            )}
          >
            {title || (
              <span className="font-sans italic text-faint">{t("editor.list.untitled")}</span>
            )}
          </span>
          {secondary && (
            <span className="mt-0.5 block truncate font-mono text-[11px] text-faint">
              {secondary}
            </span>
          )}
          {tags && <RowTags tags={tags} selected={selectedTags ?? []} />}
        </span>
        {flag && <AnalysisBadge flag={flag} analyzedAtLabel={analyzedAtLabel} />}
        {isPending && <Badge variant="warning">{t("editor.detail.edited")}</Badge>}
      </button>
      {(isPending || canDelete) && (
        <span className="absolute right-2 top-1.5 flex items-center gap-0.5 rounded-md bg-muted opacity-0 group-focus-within:opacity-100 group-hover:opacity-100 [@media(hover:none)]:opacity-100">
          {isPending && (
            <Button
              variant="ghost"
              size="icon-compact"
              aria-label={`${t("editor.detail.discard")}: ${name}`}
              title={t("editor.detail.discard")}
              onClick={onDiscard}
            >
              <X />
            </Button>
          )}
          {canDelete && (
            <Button
              variant="ghost"
              size="icon-compact"
              className="text-status-danger-foreground hover:text-status-danger-foreground"
              aria-label={`${t("editor.detail.delete")}: ${name}`}
              title={t("editor.detail.delete")}
              onClick={onDelete}
            >
              <Trash2 />
            </Button>
          )}
        </span>
      )}
    </li>
  );
}

// ---------------------------------------------------------------------------
// MasterDetailEditor component
// ---------------------------------------------------------------------------

export function MasterDetailEditor({
  connectionId,
  appId,
  currentApp,
  canUpdateApp,
  measures,
  dimensions,
  variables,
  fields,
  masterItemsHook,
  variablesHook,
  handleSaveMeasureToCache,
  handleSaveDimensionToCache,
  handleSaveVariableToCache,
  handleDeleteMeasureToCache,
  handleDeleteDimensionToCache,
  handleDeleteVariableToCache,
  initialTab,
  initialItem,
  onSelectionChange,
  pendingCount,
  onSaveAll,
  onDiscardAll,
  isSaving,
  saveDisabled,
  analysisFlags,
}: MasterDetailEditorProps) {
  const { t } = useTranslation("single-app");
  const { t: tc } = useTranslation("common");
  const { confirm, dialogProps } = useConfirmDialog();
  const { width: masterWidth, startResize, tabsRef, resizeProps } = useResizablePanelWidth();
  const { formatDate } = useFormatDate();
  const analyzedAtLabel = analysisFlags ? formatDate(analysisFlags.analyzedAt) : "";

  // ---- coordinator state ----
  const [activeTab, setActiveTab] = useState<PanelTab>(initialTab ?? "dimensions");
  const [selectedId, setSelectedId] = useState<string | null>(initialItem ?? null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [findReplaceOpen, setFindReplaceOpen] = useState(false);
  const [findReplaceShowReplace, setFindReplaceShowReplace] = useState(true);
  const [showAnalysisBadges, setShowAnalysisBadges] = useState(true);
  const highlightedRowRef = useRef<string | null>(null);
  const prevTabRef = useRef(activeTab);

  // Reset selection only when the tab actually changes. Comparing against the previous
  // value (instead of a one-shot "mounted" flag) keeps the URL-seeded item on mount even
  // under StrictMode, which double-invokes effects.
  useEffect(() => {
    if (prevTabRef.current === activeTab) return;
    prevTabRef.current = activeTab;
    setSelectedId(null);
    setSearchQuery("");
    setSelectedTypes([]);
    setSelectedTags([]);
    setFindReplaceOpen(false);
  }, [activeTab]);

  // Mirror selection into the URL from the user-action handlers (not an effect): an
  // effect fires on mount / StrictMode / route transitions, which races with navigating
  // away. Handlers fire only on real clicks — exactly how the search inputs do it.
  const handleSelect = useCallback(
    (id: string | null) => {
      setSelectedId(id);
      onSelectionChange?.(activeTab, id);
    },
    [activeTab, onSelectionChange],
  );
  const handleTabChange = useCallback(
    (tab: PanelTab) => {
      setActiveTab(tab);
      onSelectionChange?.(tab, null);
    },
    [onSelectionChange],
  );
  const detailPanelRef = useRef<HTMLDivElement>(null);
  // "Open" from the decomposition: switch tab and item together.
  const openItem = useCallback(
    (kind: GraphItemKind, id: string) => {
      const tab: PanelTab =
        kind === "measure" ? "measures" : kind === "dimension" ? "dimensions" : "variables";
      if (tab !== activeTab) {
        // Run the tab-change reset here: the effect above would also clear the selection set below.
        prevTabRef.current = tab;
        setActiveTab(tab);
        setSearchQuery("");
        setSelectedTypes([]);
        setSelectedTags([]);
        setFindReplaceOpen(false);
      }
      setSelectedId(id);
      onSelectionChange?.(tab, id);
      detailPanelRef.current?.scrollTo({ top: 0 });
    },
    [activeTab, onSelectionChange],
  );

  // ---- pending helpers for master items ----
  const hasMasterCreate = useCallback(
    (tempId: string) =>
      masterItemsHook.pendingCreates.has(`${connectionId}:${appId}:create:${tempId}`),
    [masterItemsHook.pendingCreates, connectionId, appId],
  );

  const getMasterCreate = useCallback(
    (tempId: string) =>
      masterItemsHook.pendingCreates.get(`${connectionId}:${appId}:create:${tempId}`),
    [masterItemsHook.pendingCreates, connectionId, appId],
  );

  // ---- pending helpers for variables ----
  const hasVarCreate = useCallback(
    (tempId: string) =>
      variablesHook.pendingCreates.has(`${connectionId}:${appId}:create:${tempId}`),
    [variablesHook.pendingCreates, connectionId, appId],
  );

  const getVarCreate = useCallback(
    (tempId: string) =>
      variablesHook.pendingCreates.get(`${connectionId}:${appId}:create:${tempId}`),
    [variablesHook.pendingCreates, connectionId, appId],
  );

  // ---- derived lists ----

  const measuresWithCreates = useMemo<QlikMeasure[]>(() => {
    const rows: QlikMeasure[] = [...(measures ?? [])];
    for (const create of masterItemsHook.pendingCreates.values()) {
      if (
        create.connectionId !== connectionId ||
        create.appId !== appId ||
        create.itemType !== "measure"
      )
        continue;
      rows.unshift({
        id: create.tempId,
        title: create.title,
        description: create.description,
        expression: create.expression,
        label: create.label,
        tags: create.tags ?? [],
        numberFormat: create.numberFormat,
        color: create.color,
      });
    }
    rows.sort((a, b) => a.title.localeCompare(b.title, undefined, { sensitivity: "base" }));
    return rows;
  }, [measures, masterItemsHook.pendingCreates, connectionId, appId]);

  const dimensionsWithCreates = useMemo<QlikDimension[]>(() => {
    const rows: QlikDimension[] = [...(dimensions ?? [])];
    for (const create of masterItemsHook.pendingCreates.values()) {
      if (
        create.connectionId !== connectionId ||
        create.appId !== appId ||
        create.itemType !== "dimension"
      )
        continue;
      rows.unshift({
        id: create.tempId,
        title: create.title,
        description: create.description,
        fieldDefs: create.fieldDefs ?? [],
        fieldLabels: create.fieldLabels ?? [],
        grouping: create.grouping,
        labelExpression: create.labelExpression,
        tags: create.tags ?? [],
      });
    }
    rows.sort((a, b) => a.title.localeCompare(b.title, undefined, { sensitivity: "base" }));
    return rows;
  }, [dimensions, masterItemsHook.pendingCreates, connectionId, appId]);

  const variablesWithCreates = useMemo<QlikVariable[]>(() => {
    const rows: QlikVariable[] = [...(variables ?? [])];
    for (const create of variablesHook.pendingCreates.values()) {
      if (create.connectionId !== connectionId || create.appId !== appId) continue;
      rows.unshift({
        id: create.tempId,
        name: create.name,
        definition: create.definition,
        description: create.comment,
        isScriptCreated: false,
      });
    }
    rows.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
    return rows;
  }, [variables, variablesHook.pendingCreates, connectionId, appId]);

  // ---- expression graph for the decomposition: effective items, pending drafts applied ----
  const getMasterChange = masterItemsHook.getChange;
  const getVariableChange = variablesHook.getChange;
  const expressionGraph = useMemo(() => {
    const items: GraphItemInput[] = [];
    for (const m of measuresWithCreates) {
      const change = getMasterChange(connectionId, appId, m.id);
      if (change?.action === "delete") continue;
      items.push({
        kind: "measure",
        id: m.id,
        name: change?.changes.title ?? m.title,
        sources: [
          { id: "expression", text: change?.changes.expression ?? m.expression ?? "" },
          { id: "label", text: change?.changes.label ?? m.label ?? "" },
        ],
      });
    }
    for (const d of dimensionsWithCreates) {
      const change = getMasterChange(connectionId, appId, d.id);
      if (change?.action === "delete") continue;
      const fieldDefs = change?.changes.fieldDefs ?? d.fieldDefs;
      items.push({
        kind: "dimension",
        id: d.id,
        name: change?.changes.title ?? d.title,
        sources: [
          ...fieldDefs.map((text, i) => ({ id: `field:${i}` as const, text })),
          { id: "label", text: change?.changes.labelExpression ?? d.labelExpression ?? "" },
        ],
      });
    }
    for (const v of variablesWithCreates) {
      const change = getVariableChange(connectionId, appId, v.id);
      if (change?.action === "delete") continue;
      items.push({
        kind: "variable",
        id: v.id,
        name: change?.changes.name ?? v.name,
        sources: [{ id: "expression", text: change?.changes.definition ?? v.definition ?? "" }],
      });
    }
    return buildExpressionGraph(items);
  }, [
    measuresWithCreates,
    dimensionsWithCreates,
    variablesWithCreates,
    getMasterChange,
    getVariableChange,
    connectionId,
    appId,
  ]);

  // ---- filtered lists ----

  // Tags as edited in the draft (a pending create already holds its own).
  const effectiveTags = useCallback(
    (item: { id: string; tags: string[] }) =>
      getMasterChange(connectionId, appId, item.id)?.changes.tags ?? item.tags,
    [getMasterChange, connectionId, appId],
  );

  const filteredMeasures = useMemo(() => {
    const q = searchQuery.toLowerCase();
    return measuresWithCreates.filter(
      (m) =>
        (selectedTags.length === 0 || effectiveTags(m).some((tag) => selectedTags.includes(tag))) &&
        (!q ||
          m.title.toLowerCase().includes(q) ||
          m.description?.toLowerCase().includes(q) ||
          m.expression?.toLowerCase().includes(q) ||
          m.tags.some((tag) => tag.toLowerCase().includes(q))),
    );
  }, [measuresWithCreates, searchQuery, selectedTags, effectiveTags]);

  const filteredDimensions = useMemo(() => {
    const q = searchQuery.toLowerCase();
    return dimensionsWithCreates.filter(
      (d) =>
        (selectedTags.length === 0 || effectiveTags(d).some((tag) => selectedTags.includes(tag))) &&
        (!q ||
          d.title.toLowerCase().includes(q) ||
          d.description?.toLowerCase().includes(q) ||
          d.fieldDefs.some((f) => f.toLowerCase().includes(q)) ||
          d.tags.some((tag) => tag.toLowerCase().includes(q))),
    );
  }, [dimensionsWithCreates, searchQuery, selectedTags, effectiveTags]);

  const tabTagLists = useMemo(
    () =>
      activeTab === "measures"
        ? measuresWithCreates.map(effectiveTags)
        : activeTab === "dimensions"
          ? dimensionsWithCreates.map(effectiveTags)
          : [],
    [activeTab, measuresWithCreates, dimensionsWithCreates, effectiveTags],
  );

  const filteredVariables = useMemo(() => {
    return variablesWithCreates.filter((v) => {
      const matchesSearch =
        !searchQuery ||
        v.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        v.definition?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        v.description?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesType =
        selectedTypes.length === 0 ||
        (selectedTypes.includes("script") && v.isScriptCreated) ||
        (selectedTypes.includes("user") && !v.isScriptCreated);
      return matchesSearch && matchesType;
    });
  }, [variablesWithCreates, searchQuery, selectedTypes]);

  // ---- tab counts ----
  const measureCount = measuresWithCreates.length;
  const dimensionCount = dimensionsWithCreates.length;
  const variableCount = variablesWithCreates.length;

  // ---- "+New" handler ----
  const handleNewItem = useCallback(() => {
    if (!canUpdateApp) return;
    const tempId = crypto.randomUUID();
    if (activeTab === "measures") {
      masterItemsHook.addCreate({
        connectionId,
        appId,
        appName: currentApp?.name,
        tempId,
        itemType: "measure",
        title: "",
        tags: [],
      });
      setSelectedId(tempId);
    } else if (activeTab === "dimensions") {
      masterItemsHook.addCreate({
        connectionId,
        appId,
        appName: currentApp?.name,
        tempId,
        itemType: "dimension",
        title: "",
        fieldDefs: [],
        fieldLabels: [],
        tags: [],
      });
      setSelectedId(tempId);
    } else {
      variablesHook.addCreate({
        connectionId,
        appId,
        appName: currentApp?.name,
        tempId,
        name: "",
      });
      setSelectedId(tempId);
    }
  }, [
    activeTab,
    canUpdateApp,
    connectionId,
    appId,
    currentApp?.name,
    masterItemsHook,
    variablesHook,
  ]);

  // ---- find & replace items ----
  const findReplaceItems = useMemo<FindReplaceItem[]>(() => {
    if (activeTab === "measures") {
      return filteredMeasures.flatMap((m) => {
        const isCreate = hasMasterCreate(m.id);
        const change = isCreate ? undefined : masterItemsHook.getChange(connectionId, appId, m.id);
        const create = isCreate ? getMasterCreate(m.id) : undefined;
        if (!isCreate && change?.action === "delete") return [];
        const title = isCreate ? (create?.title ?? m.title) : (change?.changes.title ?? m.title);
        const expr = isCreate
          ? (create?.expression ?? "")
          : (change?.changes.expression ?? m.expression ?? "");
        return [{ id: m.id, name: title, content: expr }];
      });
    }
    if (activeTab === "dimensions") {
      return filteredDimensions.flatMap((d) => {
        const isCreate = hasMasterCreate(d.id);
        const change = isCreate ? undefined : masterItemsHook.getChange(connectionId, appId, d.id);
        const create = isCreate ? getMasterCreate(d.id) : undefined;
        if (!isCreate && change?.action === "delete") return [];
        const title = isCreate ? (create?.title ?? d.title) : (change?.changes.title ?? d.title);
        const fieldDefs = isCreate
          ? (create?.fieldDefs ?? d.fieldDefs)
          : (change?.changes.fieldDefs ?? d.fieldDefs);
        return [{ id: d.id, name: title, content: (fieldDefs ?? []).join("\n") }];
      });
    }
    // variables
    return filteredVariables.flatMap((v) => {
      const isCreate = hasVarCreate(v.id);
      const change = isCreate ? undefined : variablesHook.getChange(connectionId, appId, v.id);
      const create = isCreate ? getVarCreate(v.id) : undefined;
      if (!isCreate && change?.action === "delete") return [];
      const name = isCreate ? (create?.name ?? v.name) : (change?.changes.name ?? v.name);
      const def = isCreate
        ? (create?.definition ?? "")
        : (change?.changes.definition ?? v.definition ?? "");
      return [{ id: v.id, name, content: def }];
    });
  }, [
    activeTab,
    filteredMeasures,
    filteredDimensions,
    filteredVariables,
    hasMasterCreate,
    getMasterCreate,
    hasVarCreate,
    getVarCreate,
    masterItemsHook,
    variablesHook,
    connectionId,
    appId,
  ]);

  // ---- keyboard shortcuts ----
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "h") {
        e.preventDefault();
        setFindReplaceShowReplace(true);
        setFindReplaceOpen(true);
      } else if ((e.ctrlKey || e.metaKey) && e.key === "f") {
        e.preventDefault();
        setFindReplaceShowReplace(false);
        setFindReplaceOpen(true);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  // ---- find & replace apply ----
  const handleReplace = useCallback(
    ({ itemId, field, newValue }: { itemId: string; field: string; newValue: string }) => {
      if (activeTab === "measures") {
        const m = filteredMeasures.find((x) => x.id === itemId);
        if (!m) return;
        if (field === "name") handleSaveMeasureToCache(m, { title: newValue });
        else handleSaveMeasureToCache(m, { expression: newValue });
      } else if (activeTab === "dimensions") {
        const d = filteredDimensions.find((x) => x.id === itemId);
        if (!d) return;
        if (field === "name") handleSaveDimensionToCache(d, { title: newValue });
        else handleSaveDimensionToCache(d, { fieldDefs: newValue.split("\n").filter(Boolean) });
      } else {
        const v = filteredVariables.find((x) => x.id === itemId);
        if (!v) return;
        if (field === "name") handleSaveVariableToCache(v, { name: newValue });
        else handleSaveVariableToCache(v, { definition: newValue });
      }
    },
    [
      activeTab,
      filteredMeasures,
      filteredDimensions,
      filteredVariables,
      handleSaveMeasureToCache,
      handleSaveDimensionToCache,
      handleSaveVariableToCache,
    ],
  );

  const handleReplaceAll = useCallback(
    (replacements: { itemId: string; field: string; newValue: string }[]) => {
      for (const r of replacements) handleReplace(r);
    },
    [handleReplace],
  );

  // ---- selected item lookup ----
  const selectedMeasure = useMemo(
    () =>
      activeTab === "measures"
        ? (measuresWithCreates.find((m) => m.id === selectedId) ?? null)
        : null,
    [activeTab, measuresWithCreates, selectedId],
  );
  const selectedDimension = useMemo(
    () =>
      activeTab === "dimensions"
        ? (dimensionsWithCreates.find((d) => d.id === selectedId) ?? null)
        : null,
    [activeTab, dimensionsWithCreates, selectedId],
  );
  const selectedVariable = useMemo(
    () =>
      activeTab === "variables"
        ? (variablesWithCreates.find((v) => v.id === selectedId) ?? null)
        : null,
    [activeTab, variablesWithCreates, selectedId],
  );

  // ---- type options for variable filter ----
  const typeOptions: SelectOption[] = [
    { label: t("variables.typeUserDefined"), value: "user" },
    { label: t("variables.typeScript"), value: "script" },
  ];

  // ---- detail form onChange wiring ----
  const handleMeasureChange = useCallback(
    (changes: {
      title?: string;
      description?: string;
      expression?: string;
      label?: string;
      tags?: string[];
      numberFormat?: string;
      color?: string;
    }) => {
      if (!selectedMeasure) return;
      const isCreate = hasMasterCreate(selectedMeasure.id);
      if (isCreate) {
        masterItemsHook.updateCreate(connectionId, appId, selectedMeasure.id, changes);
      } else {
        handleSaveMeasureToCache(selectedMeasure, changes);
      }
    },
    [
      selectedMeasure,
      hasMasterCreate,
      masterItemsHook,
      connectionId,
      appId,
      handleSaveMeasureToCache,
    ],
  );

  const handleDimensionChange = useCallback(
    (changes: {
      title?: string;
      description?: string;
      fieldDefs?: string[];
      fieldLabels?: string[];
      grouping?: "N" | "H" | "C";
      labelExpression?: string;
      tags?: string[];
    }) => {
      if (!selectedDimension) return;
      const isCreate = hasMasterCreate(selectedDimension.id);
      if (isCreate) {
        masterItemsHook.updateCreate(connectionId, appId, selectedDimension.id, changes);
      } else {
        handleSaveDimensionToCache(selectedDimension, changes);
      }
    },
    [
      selectedDimension,
      hasMasterCreate,
      masterItemsHook,
      connectionId,
      appId,
      handleSaveDimensionToCache,
    ],
  );

  const handleVariableChange = useCallback(
    (changes: { name?: string; definition?: string; comment?: string }) => {
      if (!selectedVariable) return;
      const isCreate = hasVarCreate(selectedVariable.id);
      if (isCreate) {
        variablesHook.updateCreate(connectionId, appId, selectedVariable.id, changes);
      } else {
        handleSaveVariableToCache(selectedVariable, changes);
      }
    },
    [selectedVariable, hasVarCreate, variablesHook, connectionId, appId, handleSaveVariableToCache],
  );

  // ---- delete handlers ----
  const handleDeleteMeasure = useCallback(
    (m: QlikMeasure) => {
      confirm({
        title: t("masterItems.deleteMeasureTitle"),
        description: t("masterItems.deleteMeasureDescription", { name: m.title }),
        confirmLabel: t("masterItems.deleteConfirm"),
        variant: "destructive",
        onConfirm: () => {
          handleDeleteMeasureToCache(m);
          if (selectedId === m.id) setSelectedId(null);
        },
      });
    },
    [confirm, t, handleDeleteMeasureToCache, selectedId],
  );

  const handleDeleteDimension = useCallback(
    (d: QlikDimension) => {
      confirm({
        title: t("masterItems.deleteDimensionTitle"),
        description: t("masterItems.deleteDimensionDescription", { name: d.title }),
        confirmLabel: t("masterItems.deleteConfirm"),
        variant: "destructive",
        onConfirm: () => {
          handleDeleteDimensionToCache(d);
          if (selectedId === d.id) setSelectedId(null);
        },
      });
    },
    [confirm, t, handleDeleteDimensionToCache, selectedId],
  );

  const handleDeleteVariable = useCallback(
    (v: QlikVariable) => {
      confirm({
        title: t("variables.deleteTitle"),
        description: t("variables.deleteDescription", { name: v.name }),
        confirmLabel: t("variables.deleteConfirm"),
        variant: "destructive",
        onConfirm: () => {
          handleDeleteVariableToCache(v);
          if (selectedId === v.id) setSelectedId(null);
        },
      });
    },
    [confirm, t, handleDeleteVariableToCache, selectedId],
  );

  // ---- detail panel header text ----
  let detailEyebrow = "";
  let detailHasChanges = false;
  if (activeTab === "measures" && selectedMeasure) {
    const isCreate = hasMasterCreate(selectedMeasure.id);
    const change = isCreate
      ? undefined
      : masterItemsHook.getChange(connectionId, appId, selectedMeasure.id);
    detailHasChanges = isCreate || !!change;
    detailEyebrow = isCreate ? t("editor.detail.newMeasure") : t("editor.detail.editingMeasure");
  } else if (activeTab === "dimensions" && selectedDimension) {
    const isCreate = hasMasterCreate(selectedDimension.id);
    const change = isCreate
      ? undefined
      : masterItemsHook.getChange(connectionId, appId, selectedDimension.id);
    detailHasChanges = isCreate || !!change;
    detailEyebrow = isCreate
      ? t("editor.detail.newDimension")
      : t("editor.detail.editingDimension");
  } else if (activeTab === "variables" && selectedVariable) {
    const isCreate = hasVarCreate(selectedVariable.id);
    const change = isCreate
      ? undefined
      : variablesHook.getChange(connectionId, appId, selectedVariable.id);
    detailHasChanges = isCreate || !!change;
    detailEyebrow = isCreate ? t("editor.detail.newVariable") : t("editor.detail.editingVariable");
  }

  // ---- empty state label per tab ----
  const emptyDetailLabel =
    activeTab === "measures"
      ? t("editor.detail.emptyMeasure")
      : activeTab === "dimensions"
        ? t("editor.detail.emptyDimension")
        : t("editor.detail.emptyVariable");

  const newItemLabel =
    activeTab === "measures"
      ? t("masterItems.newMeasure")
      : activeTab === "dimensions"
        ? t("masterItems.newDimension")
        : t("variables.newVariable");

  const hasSelection = selectedMeasure ?? selectedDimension ?? selectedVariable;

  // The hook measures the tab row (never narrower than its labels need); the library Tabs
  // only exposes its root, so find the tablist inside it. Stable, so it attaches once.
  const tabsListRef = useCallback(
    (el: HTMLDivElement | null) =>
      tabsRef(el?.querySelector<HTMLElement>('[role="tablist"]') ?? null),
    [tabsRef],
  );

  // ---- the three panes of the master list, one per tab ----
  const toolbar = (
    <div className="flex items-center gap-2 px-3 py-2.5">
      <SearchInput
        containerClassName="min-w-0 flex-1"
        value={searchQuery}
        onValueChange={setSearchQuery}
        clearLabel={tc("clearSearch")}
        placeholder={
          activeTab === "variables"
            ? t("variables.searchPlaceholder")
            : activeTab === "measures"
              ? t("masterItems.searchMeasuresPlaceholder")
              : t("masterItems.searchDimensionsPlaceholder")
        }
        aria-label={
          activeTab === "variables"
            ? t("variables.searchPlaceholder")
            : activeTab === "measures"
              ? t("masterItems.searchMeasuresPlaceholder")
              : t("masterItems.searchDimensionsPlaceholder")
        }
      />
      {canUpdateApp && (
        <Button
          variant="outline"
          size="icon-sm"
          className="shrink-0"
          aria-label={newItemLabel}
          title={newItemLabel}
          onClick={handleNewItem}
        >
          <Plus />
        </Button>
      )}
    </div>
  );

  const typeFilter =
    activeTab === "variables" ? (
      <MultiSelect<string>
        aria-label={t("variables.filterTypePlaceholder")}
        size="compact"
        className="w-full"
        options={typeOptions}
        value={selectedTypes}
        onValueChange={setSelectedTypes}
        messages={pickerMessages({ placeholder: t("variables.filterTypePlaceholder") })}
      />
    ) : (
      <TagFilter tagLists={tabTagLists} value={selectedTags} onValueChange={setSelectedTags} />
    );

  const lists = (
    <div className="flex min-h-0 flex-1 flex-col">
      {toolbar}
      <div className="px-3 pb-2.5 empty:hidden">{typeFilter}</div>
      <div className="min-h-0 flex-1 overflow-y-auto border-t border-hairline-soft">
        {activeTab === "measures" && (
          <MeasureList
            items={filteredMeasures}
            selectedId={selectedId}
            onSelect={handleSelect}
            connectionId={connectionId}
            appId={appId}
            canUpdateApp={canUpdateApp}
            hasMasterCreate={hasMasterCreate}
            getMasterCreate={getMasterCreate}
            getChange={masterItemsHook.getChange}
            flags={showAnalysisBadges ? analysisFlags?.measures : undefined}
            selectedTags={selectedTags}
            analyzedAtLabel={analyzedAtLabel}
            onDiscard={(m) => {
              const isCreate = hasMasterCreate(m.id);
              if (isCreate) {
                masterItemsHook.removeCreate(connectionId, appId, m.id);
                if (selectedId === m.id) setSelectedId(null);
              } else {
                masterItemsHook.removeChange(connectionId, appId, m.id);
              }
            }}
            onDelete={handleDeleteMeasure}
          />
        )}
        {activeTab === "dimensions" && (
          <DimensionList
            items={filteredDimensions}
            selectedId={selectedId}
            onSelect={handleSelect}
            connectionId={connectionId}
            appId={appId}
            canUpdateApp={canUpdateApp}
            hasMasterCreate={hasMasterCreate}
            getMasterCreate={getMasterCreate}
            getChange={masterItemsHook.getChange}
            flags={showAnalysisBadges ? analysisFlags?.dimensions : undefined}
            selectedTags={selectedTags}
            analyzedAtLabel={analyzedAtLabel}
            onDiscard={(d) => {
              const isCreate = hasMasterCreate(d.id);
              if (isCreate) {
                masterItemsHook.removeCreate(connectionId, appId, d.id);
                if (selectedId === d.id) setSelectedId(null);
              } else {
                masterItemsHook.removeChange(connectionId, appId, d.id);
              }
            }}
            onDelete={handleDeleteDimension}
          />
        )}
        {activeTab === "variables" && (
          <VariableList
            items={filteredVariables}
            selectedId={selectedId}
            onSelect={handleSelect}
            connectionId={connectionId}
            appId={appId}
            canUpdateApp={canUpdateApp}
            hasVarCreate={hasVarCreate}
            getVarCreate={getVarCreate}
            getChange={variablesHook.getChange}
            flags={showAnalysisBadges ? analysisFlags?.variables : undefined}
            analyzedAtLabel={analyzedAtLabel}
            onDiscard={(v) => {
              const isCreate = hasVarCreate(v.id);
              if (isCreate) {
                variablesHook.removeCreate(connectionId, appId, v.id);
                if (selectedId === v.id) setSelectedId(null);
              } else {
                variablesHook.removeChange(connectionId, appId, v.id);
              }
            }}
            onDelete={handleDeleteVariable}
          />
        )}
      </div>
    </div>
  );

  const tabLabel = (label: string, count: number): ReactNode => (
    <>
      {label} <span className="font-mono text-[10px] text-faint">{count}</span>
    </>
  );

  // ---- detail header actions: discard the selected item's change / delete it ----
  const discardSelected = () => {
    if (activeTab === "measures" && selectedMeasure) {
      if (hasMasterCreate(selectedMeasure.id)) {
        masterItemsHook.removeCreate(connectionId, appId, selectedMeasure.id);
        setSelectedId(null);
      } else {
        masterItemsHook.removeChange(connectionId, appId, selectedMeasure.id);
      }
    } else if (activeTab === "dimensions" && selectedDimension) {
      if (hasMasterCreate(selectedDimension.id)) {
        masterItemsHook.removeCreate(connectionId, appId, selectedDimension.id);
        setSelectedId(null);
      } else {
        masterItemsHook.removeChange(connectionId, appId, selectedDimension.id);
      }
    } else if (activeTab === "variables" && selectedVariable) {
      if (hasVarCreate(selectedVariable.id)) {
        variablesHook.removeCreate(connectionId, appId, selectedVariable.id);
        setSelectedId(null);
      } else {
        variablesHook.removeChange(connectionId, appId, selectedVariable.id);
      }
    }
  };

  // Delete: not for pending creates, not twice, and script variables are read-only.
  let deleteSelected: (() => void) | null = null;
  if (canUpdateApp) {
    if (
      activeTab === "measures" &&
      selectedMeasure &&
      !hasMasterCreate(selectedMeasure.id) &&
      masterItemsHook.getChange(connectionId, appId, selectedMeasure.id)?.action !== "delete"
    ) {
      deleteSelected = () => handleDeleteMeasure(selectedMeasure);
    } else if (
      activeTab === "dimensions" &&
      selectedDimension &&
      !hasMasterCreate(selectedDimension.id) &&
      masterItemsHook.getChange(connectionId, appId, selectedDimension.id)?.action !== "delete"
    ) {
      deleteSelected = () => handleDeleteDimension(selectedDimension);
    } else if (
      activeTab === "variables" &&
      selectedVariable &&
      !hasVarCreate(selectedVariable.id) &&
      !selectedVariable.isScriptCreated &&
      variablesHook.getChange(connectionId, appId, selectedVariable.id)?.action !== "delete"
    ) {
      deleteSelected = () => handleDeleteVariable(selectedVariable);
    }
  }

  return (
    // The whole workspace is one card on the gray canvas: toolbar on top, the
    // two panes filling the remaining height.
    <div className="flex min-h-0 min-w-[640px] flex-1 flex-col overflow-hidden rounded-xl border border-hairline bg-surface">
      {/* ------------------------------------------------------------------ */}
      {/* ACTION ROW — find & replace, discard, save */}
      {/* ------------------------------------------------------------------ */}
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-b border-hairline px-5 py-[13px]">
        <div className="flex items-center gap-2.5">
          <h2 className="text-[15px] font-bold text-ink">{t("editor.actions.title")}</h2>
          {pendingCount > 0 && (
            <Badge variant="warning" className="font-mono">
              {t("editor.actions.unsaved", { count: pendingCount })}
            </Badge>
          )}
          {analysisFlags && (
            <div className="border-l border-hairline pl-2.5">
              <Field
                inline
                label={
                  <span className="flex items-center gap-1.5 whitespace-nowrap text-[12px] font-normal text-dim">
                    {t("analysisFlags.toggleLabel")}
                    <span className="font-mono text-[11px] text-faint">· {analyzedAtLabel}</span>
                  </span>
                }
              >
                <Switch checked={showAnalysisBadges} onCheckedChange={setShowAnalysisBadges} />
              </Field>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setFindReplaceShowReplace(true);
              setFindReplaceOpen(true);
            }}
            title={t("editor.actions.findReplace")}
          >
            <Replace />
            {t("editor.actions.findReplace")}
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="text-status-danger-foreground hover:text-status-danger-foreground"
            disabled={pendingCount === 0 || isSaving}
            onClick={onDiscardAll}
          >
            <Trash2 />
            {t("editor.actions.discardAll")}
          </Button>
          <Button
            size="sm"
            loading={isSaving}
            disabled={saveDisabled || pendingCount === 0}
            onClick={(event) => onSaveAll(event.currentTarget)}
          >
            {t("editor.actions.saveAll", { count: pendingCount })}
          </Button>
        </div>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* FIND & REPLACE (floating widget) over the master/detail */}
      {/* ------------------------------------------------------------------ */}
      <div className="relative flex min-h-0 flex-1 flex-row overflow-hidden">
        {findReplaceOpen && (
          <div className="absolute right-4 top-4 z-20">
            <FindReplaceBar
              items={findReplaceItems}
              showReplace={findReplaceShowReplace}
              onClose={() => {
                setFindReplaceOpen(false);
              }}
              onMatchesChange={() => {
                // no-op: highlights handled via highlightedRowRef
              }}
              onHighlight={(itemId) => {
                highlightedRowRef.current = itemId;
              }}
              onReplace={handleReplace}
              onReplaceAll={handleReplaceAll}
            />
          </div>
        )}
        {/* ------------------------------------------------------------------ */}
        {/* MASTER PANEL */}
        {/* ------------------------------------------------------------------ */}
        <div className="flex shrink-0 flex-col" style={{ width: masterWidth }}>
          <Tabs
            // The resizable width never goes below what the tab labels need.
            ref={tabsListRef}
            className={cn(
              "min-h-0 flex-1",
              "[&>[role=tablist]]:gap-4 [&>[role=tablist]]:px-3 [&>[role=tablist]]:pt-2",
              "[&>[role=tabpanel]]:flex [&>[role=tabpanel]]:min-h-0 [&>[role=tabpanel]]:flex-1 [&>[role=tabpanel]]:flex-col [&>[role=tabpanel]]:pt-0",
            )}
            label={t("editor.tabs.label")}
            activationMode="automatic"
            value={activeTab}
            onValueChange={(v) => handleTabChange(v as PanelTab)}
            items={[
              {
                value: "dimensions",
                label: tabLabel(t("editor.tabs.dimensions"), dimensionCount),
                content: lists,
              },
              {
                value: "measures",
                label: tabLabel(t("editor.tabs.measures"), measureCount),
                content: lists,
              },
              {
                value: "variables",
                label: tabLabel(t("editor.tabs.variables"), variableCount),
                content: lists,
              },
            ]}
          />
        </div>

        <ResizeHandle onPointerDown={startResize} {...resizeProps} />

        {/* ------------------------------------------------------------------ */}
        {/* DETAIL PANEL */}
        {/* ------------------------------------------------------------------ */}
        <div
          ref={detailPanelRef}
          className="@container flex min-w-0 flex-1 flex-col overflow-y-auto"
        >
          {hasSelection ? (
            <>
              {/* Detail header */}
              <div className="sticky top-0 z-10 flex items-center justify-between gap-2 bg-surface px-5 pb-3 pt-4">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-faint">
                    {detailEyebrow}
                  </span>
                  {detailHasChanges && (
                    <Badge variant="warning" className="font-mono">
                      {t("editor.detail.edited")}
                    </Badge>
                  )}
                </div>
                {/* Discard/Delete actions */}
                <div className="flex items-center gap-1">
                  {detailHasChanges && (
                    <Button
                      variant="ghost"
                      size="compact"
                      title={t("masterItems.discardChange")}
                      onClick={discardSelected}
                    >
                      <X />
                      {t("editor.detail.discard")}
                    </Button>
                  )}
                  {deleteSelected && (
                    <Button
                      variant="ghost"
                      size="compact"
                      className="text-status-danger-foreground hover:text-status-danger-foreground"
                      onClick={deleteSelected}
                    >
                      <Trash2 />
                      {t("editor.detail.delete")}
                    </Button>
                  )}
                </div>
              </div>

              {/* Detail form — capped width; on a wide panel the decomposition sits beside it */}
              <div className={DETAIL_GRID}>
                <div className="min-w-0">
                  {activeTab === "measures" && selectedMeasure && (
                    <MeasureDetailForm
                      item={selectedMeasure}
                      isCreate={hasMasterCreate(selectedMeasure.id)}
                      existingChange={masterItemsHook.getChange(
                        connectionId,
                        appId,
                        selectedMeasure.id,
                      )}
                      existingCreate={getMasterCreate(selectedMeasure.id)}
                      canEdit={canUpdateApp}
                      onChange={handleMeasureChange}
                    />
                  )}
                  {activeTab === "dimensions" && selectedDimension && (
                    <DimensionDetailForm
                      item={selectedDimension}
                      isCreate={hasMasterCreate(selectedDimension.id)}
                      existingChange={masterItemsHook.getChange(
                        connectionId,
                        appId,
                        selectedDimension.id,
                      )}
                      existingCreate={getMasterCreate(selectedDimension.id)}
                      canEdit={canUpdateApp}
                      onChange={handleDimensionChange}
                      fields={fields}
                    />
                  )}
                  {activeTab === "variables" && selectedVariable && (
                    <VariableDetailForm
                      item={selectedVariable}
                      isCreate={hasVarCreate(selectedVariable.id)}
                      existingChange={variablesHook.getChange(
                        connectionId,
                        appId,
                        selectedVariable.id,
                      )}
                      existingCreate={getVarCreate(selectedVariable.id)}
                      canEdit={canUpdateApp}
                      onChange={handleVariableChange}
                    />
                  )}
                </div>
                <ExpressionDecomposition
                  key={hasSelection.id}
                  graph={expressionGraph}
                  itemId={hasSelection.id}
                  scope="app"
                  onOpenItem={openItem}
                />
              </div>
            </>
          ) : (
            <div className="flex flex-1 items-center justify-center p-6">
              <FeedbackState
                state="empty"
                icon={<MousePointerClick />}
                title={emptyDetailLabel}
                description={t("editor.detail.emptyHint")}
                action={
                  canUpdateApp ? (
                    <Button variant="outline" size="sm" onClick={handleNewItem}>
                      <Plus />
                      {newItemLabel}
                    </Button>
                  ) : undefined
                }
              />
            </div>
          )}
        </div>
      </div>

      <ConfirmDialog {...dialogProps} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// List sub-components
// ---------------------------------------------------------------------------

function EmptyList({ icon, label }: { icon: ReactNode; label: string }) {
  return <FeedbackState state="empty" icon={icon} title={label} />;
}

function MeasureList({
  items,
  selectedId,
  onSelect,
  connectionId,
  appId,
  canUpdateApp,
  hasMasterCreate,
  getMasterCreate,
  getChange,
  flags,
  selectedTags,
  analyzedAtLabel,
  onDiscard,
  onDelete,
}: {
  items: QlikMeasure[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  connectionId: string;
  appId: string;
  canUpdateApp: boolean;
  hasMasterCreate: (id: string) => boolean;
  getMasterCreate: (id: string) => PendingMasterItemCreate | undefined;
  getChange: (connId: string, appId: string, id: string) => PendingMasterItemChange | undefined;
  flags?: Map<string, AnalysisItemFlag>;
  selectedTags: string[];
  analyzedAtLabel: string;
  onDiscard: (m: QlikMeasure) => void;
  onDelete: (m: QlikMeasure) => void;
}) {
  const { t } = useTranslation("single-app");
  if (items.length === 0) {
    return <EmptyList icon={<Calculator />} label={t("editor.list.noMeasures")} />;
  }

  return (
    <ul className="divide-y divide-hairline-soft">
      {items.map((m) => {
        const isCreate = hasMasterCreate(m.id);
        const change = isCreate ? undefined : getChange(connectionId, appId, m.id);
        const create = isCreate ? getMasterCreate(m.id) : undefined;
        const isPending = isCreate || !!change;
        const isPendingDelete = !isCreate && change?.action === "delete";
        const title = isCreate ? (create?.title ?? m.title) : (change?.changes.title ?? m.title);
        const expr = isCreate
          ? (create?.expression ?? m.expression ?? "")
          : (change?.changes.expression ?? m.expression ?? "");
        const tags = isCreate ? (create?.tags ?? m.tags) : (change?.changes.tags ?? m.tags);

        return (
          <MasterRow
            key={m.id}
            title={title}
            secondary={expr}
            tags={tags}
            selectedTags={selectedTags}
            selected={selectedId === m.id}
            isPending={isPending}
            isPendingDelete={isPendingDelete}
            flag={!isPending && !isCreate ? flags?.get(m.id) : undefined}
            analyzedAtLabel={analyzedAtLabel}
            canDelete={!isCreate && canUpdateApp && !isPendingDelete}
            onSelect={() => onSelect(m.id)}
            onDiscard={() => onDiscard(m)}
            onDelete={() => onDelete(m)}
          />
        );
      })}
    </ul>
  );
}

function DimensionList({
  items,
  selectedId,
  onSelect,
  connectionId,
  appId,
  canUpdateApp,
  hasMasterCreate,
  getMasterCreate,
  getChange,
  flags,
  selectedTags,
  analyzedAtLabel,
  onDiscard,
  onDelete,
}: {
  items: QlikDimension[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  connectionId: string;
  appId: string;
  canUpdateApp: boolean;
  hasMasterCreate: (id: string) => boolean;
  getMasterCreate: (id: string) => PendingMasterItemCreate | undefined;
  getChange: (connId: string, appId: string, id: string) => PendingMasterItemChange | undefined;
  flags?: Map<string, AnalysisItemFlag>;
  selectedTags: string[];
  analyzedAtLabel: string;
  onDiscard: (d: QlikDimension) => void;
  onDelete: (d: QlikDimension) => void;
}) {
  const { t } = useTranslation("single-app");
  if (items.length === 0) {
    return <EmptyList icon={<Ruler />} label={t("editor.list.noDimensions")} />;
  }

  return (
    <ul className="divide-y divide-hairline-soft">
      {items.map((d) => {
        const isCreate = hasMasterCreate(d.id);
        const change = isCreate ? undefined : getChange(connectionId, appId, d.id);
        const create = isCreate ? getMasterCreate(d.id) : undefined;
        const isPending = isCreate || !!change;
        const isPendingDelete = !isCreate && change?.action === "delete";
        const title = isCreate ? (create?.title ?? d.title) : (change?.changes.title ?? d.title);
        const fieldDefs = isCreate
          ? (create?.fieldDefs ?? d.fieldDefs)
          : (change?.changes.fieldDefs ?? d.fieldDefs);
        const tags = isCreate ? (create?.tags ?? d.tags) : (change?.changes.tags ?? d.tags);

        return (
          <MasterRow
            key={d.id}
            title={title}
            secondary={(fieldDefs ?? []).join(", ")}
            tags={tags}
            selectedTags={selectedTags}
            selected={selectedId === d.id}
            isPending={isPending}
            isPendingDelete={isPendingDelete}
            flag={!isPending && !isCreate ? flags?.get(d.id) : undefined}
            analyzedAtLabel={analyzedAtLabel}
            canDelete={!isCreate && canUpdateApp && !isPendingDelete}
            onSelect={() => onSelect(d.id)}
            onDiscard={() => onDiscard(d)}
            onDelete={() => onDelete(d)}
          />
        );
      })}
    </ul>
  );
}

function VariableList({
  items,
  selectedId,
  onSelect,
  connectionId,
  appId,
  canUpdateApp,
  hasVarCreate,
  getVarCreate,
  getChange,
  flags,
  analyzedAtLabel,
  onDiscard,
  onDelete,
}: {
  items: QlikVariable[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  connectionId: string;
  appId: string;
  canUpdateApp: boolean;
  hasVarCreate: (id: string) => boolean;
  getVarCreate: (
    id: string,
  ) => import("@/stores/pending-changes").PendingVariableCreate | undefined;
  getChange: (
    connId: string,
    appId: string,
    id: string,
  ) => import("@/stores/pending-changes").PendingVariableChange | undefined;
  flags?: Map<string, AnalysisItemFlag>;
  analyzedAtLabel: string;
  onDiscard: (v: QlikVariable) => void;
  onDelete: (v: QlikVariable) => void;
}) {
  const { t } = useTranslation("single-app");
  if (items.length === 0) {
    return <EmptyList icon={<Code />} label={t("editor.list.noVariables")} />;
  }

  return (
    <ul className="divide-y divide-hairline-soft">
      {items.map((v) => {
        const isCreate = hasVarCreate(v.id);
        const change = isCreate ? undefined : getChange(connectionId, appId, v.id);
        const create = isCreate ? getVarCreate(v.id) : undefined;
        const isPending = isCreate || !!change;
        const isPendingDelete = !isCreate && change?.action === "delete";
        const rawFlag = !isPending && !isCreate ? flags?.get(v.id) : undefined;
        // Reserved/config variables are expected to be unused, so never surface the "unused"
        // badge for them — but still surface "broken" if the analysis flagged an expression error.
        const analysisFlag =
          rawFlag && (v.isReserved || v.isConfig)
            ? rawFlag.broken
              ? { unused: false, broken: true, indirect: false }
              : undefined
            : rawFlag;
        const name = isCreate ? (create?.name ?? v.name) : (change?.changes.name ?? v.name);
        const def = isCreate
          ? (create?.definition ?? v.definition ?? "")
          : (change?.changes.definition ?? v.definition ?? "");

        return (
          <MasterRow
            key={v.id}
            title={name}
            secondary={def}
            mono
            selected={selectedId === v.id}
            isPending={isPending}
            isPendingDelete={isPendingDelete}
            flag={analysisFlag}
            analyzedAtLabel={analyzedAtLabel}
            canDelete={!isCreate && canUpdateApp && !isPendingDelete && !v.isScriptCreated}
            onSelect={() => onSelect(v.id)}
            onDiscard={() => onDiscard(v)}
            onDelete={() => onDelete(v)}
          />
        );
      })}
    </ul>
  );
}
